import { useEffect, useState } from "react";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { HoardexaLoader } from "./HoardexaLoader";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";

export function Layout({
  title,
  subtitle,
  actions,
  children,
  topbar,
  mainClassName = "p-6 md:p-8", // Swiss / Standard SaaS padding
}) {
  const { user, exitGhostSession } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    const onLoading = (event) => setLoading(Boolean(event.detail?.loading));
    window.addEventListener("hx:api-loading", onLoading);
    return () => window.removeEventListener("hx:api-loading", onLoading);
  }, []);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    setShowLoader(loading);
  }, [loading]);

  const handleMenuClick = () => {
    if (window.innerWidth < 768) {
      setMobileOpen(true);
    } else {
      setCollapsed((value) => !value);
    }
  };

  const exitGhost = async () => {
    const advertiserID = user?.ghost_session?.advertiser_id;
    await exitGhostSession();
    navigate(advertiserID ? `/super-admin/advertisers/${advertiserID}` : "/super-admin");
  };

  return (
    <div
      className="fixed inset-0 flex overflow-hidden bg-[#F9FAFB] text-[#0F172A] hx-app-shell"
      style={{
        fontFamily:
          '"Manrope", "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {topbar ? (
          topbar({ onMenuClick: handleMenuClick })
        ) : (
          <Topbar
            title={title}
            subtitle={subtitle}
            actions={actions}
            onMenuClick={handleMenuClick}
          />
        )}

        <main
          className={`relative min-h-0 flex-1 overflow-y-auto ${mainClassName}`}
          data-testid="main-content"
        >
          <AnimatePresence>
            {showLoader && (
              <div
                className="fixed bottom-0 right-0 top-[72px] z-30 flex items-center justify-center bg-[#F9FAFB]/75"
                style={{ left: collapsed ? 70 : 256 }}
              >
                <HoardexaLoader label="Loading page data" compact />
              </div>
            )}
          </AnimatePresence>

          {user?.ghost_session && (
            <div className="mb-4 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                  <Eye size={16} />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-black">Ghost view: {user.ghost_session.advertiser_name || "Advertiser account"}</div>
                  <div className="mt-0.5 text-xs font-semibold text-amber-800">Read-only superadmin session. Advertiser audit logs are not updated.</div>
                </div>
              </div>
              <button type="button" onClick={exitGhost} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-black text-amber-900 transition hover:bg-amber-100">
                <LogOut size={14} /> Exit Ghost View
              </button>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="w-full"
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
